import React, { useState, useEffect, memo, useCallback } from 'react';
import {
  Form,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Textarea,
  Checkbox,
  Toggle,
  Container,
  Header,
  Icon,
} from '@cloudscape-design/components';
import {
  Tag,
  TagValue,
  ValuesMap,
  CreateTagRequest,
  UpdateTagRequest,
} from '../../types/helios/Tags';
import { useTags } from '../../store/tags/useTags';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import useUserProfile from 'src/hooks/useUserProfile';
import { useFormValidation } from 'src/hooks/useFormValidation';
import { TagFormValidator } from 'src/validations/TagFormValidator';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import BusinessUnitListDropdown from '../businessUnit/BusinessUnitListDropdown';
import { checkFeaturePermission } from 'src/utils/generalUtils';
import permissionConfig from 'src/contstant/permissionAccessConfig';
import { Persona } from 'src/contstant/Persona';
import { useUsers } from 'src/store/users/useUsers';
import RequiredFieldLabel from '../RequiredFieldLabel';
import { DUPLICATE_TAG_VALUE_ERROR } from '../../validations/TagFormValidator';
import { normalizeForComparison } from '../../utils/stringUtils';

interface FormErrors {
  duplicateTagValue?: string | null;
  // Add other error types as needed
}

interface TagDetailsFormProps {
  tag?: Tag | null;
  onViewDismiss: () => void;
  onTagValueAdded?: () => void;
}

interface TagParams {
  tagId: string;
  tagGroupName: string;
  businessUnitId: string;
  description: string;
  programId: string;
  values: ValuesMap;
  currentTagKey: string;
  currentTagDescription: string;
  currentTagIsActive: boolean;
  isActive: boolean;
  isMandatoryForQuestions: boolean;
  isMandatoryForAuditTemplates: boolean;
  isApplicableForAuditTemplate: boolean;
  maxTagsPerTemplate: number | null;
}

export const closeTagForm = () => {
  document.dispatchEvent(new CustomEvent('closeTagForm', {}));
};

const TagDetailsForm = memo(
  ({ tag, onViewDismiss, onTagValueAdded }: TagDetailsFormProps) => {
    const { closeView } = useSplitPanel();
    const { getUserId, getUserProgramId, getPersona, getUserBusinessUnitId } =
      useUserProfile();
    const { businessUnitList, fetchBusinessUnitApiStatus, loadBusinessUnits } =
      useBusinessUnit();

    const {
      createTag,
      updateTag,
      createTagApiStatus,
      updateTagApiStatus,
      clearAPIStatusMessage,
    } = useTags();

    // Always use the selected business unit from profile page
    const defaultBusinessUnit = getUserBusinessUnitId() || '';
    // Initialize form values
    const initValues: TagParams = {
      tagId: tag?.tagId || '',
      tagGroupName: tag?.tagGroupName || '',
      businessUnitId: tag?.businessUnit || defaultBusinessUnit,
      description: tag?.description || '',
      programId: tag?.programId || getUserProgramId() || '',
      values: tag?.values || {},
      currentTagKey: '',
      currentTagDescription: '',
      currentTagIsActive: true,
      isActive: tag?.isActive ?? true,
      isMandatoryForQuestions: tag?.isMandatoryForQuestions || false,
      isMandatoryForAuditTemplates: tag?.isMandatoryForAuditTemplates || false,
      isApplicableForAuditTemplate: tag?.isApplicableForAuditTemplate || false,
      maxTagsPerTemplate: tag?.maxTagsPerTemplate ?? null,
    };

    const [tagParams, setTagParams] = useState<TagParams>(initValues);
    const [editingTagKey, setEditingTagKey] = useState<string | null>(null);
    const [formErrors, setFormErrors] = useState<FormErrors>({});

    // Form validation
    const { fieldValidationError, validate } = useFormValidation(
      tagParams,
      TagFormValidator(!!tag),
    );

    const { checkUserPermissions, userPermission } = useUsers();

    const isEditTag = useCallback(() => {
      return checkFeaturePermission(
        getPersona(),
        permissionConfig.TAGS.EDIT_TAGS,
        userPermission?.permissions,
      );
    }, [userPermission?.permissions, getPersona]);

    const handleCloseTagForm = () => {
      closeView();
      onViewDismiss();
    };

    useEffect(() => {
      setTagParams(initValues);
    }, [tag]);

    // Track if we're in a save operation (not just adding tag values)
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (updateTagApiStatus.isSuccess || createTagApiStatus.isSuccess) {
        onTagValueAdded?.();
        // Only close panel if this was a save operation, not just adding tag values
        if (isSaving) {
          closeView();
          onViewDismiss();
        }
        setIsSaving(false);
      }
    }, [
      updateTagApiStatus.isSuccess,
      createTagApiStatus.isSuccess,
      onTagValueAdded,
      closeView,
      onViewDismiss,
      isSaving,
    ]);

    useEffect(() => {
      loadBusinessUnits({
        programId: getUserProgramId(),
        isActive: true,
        isDeleted: false,
      });
    }, []);

    // Set default business unit after business units are loaded
    useEffect(() => {
      if (!tag && businessUnitList.length > 0 && !tagParams.businessUnitId) {
        const userBusinessUnitName = getUserBusinessUnitId();
        const userBusinessUnit = businessUnitList.find(
          (bu) => bu.name === userBusinessUnitName,
        );

        if (userBusinessUnit) {
          setTagParams((prev) => ({
            ...prev,
            businessUnitId: userBusinessUnit.businessUnitId,
          }));
        }
      }
    }, [businessUnitList, tag]);

    useEffect(() => {
      document.addEventListener('closeTagForm', handleCloseTagForm);
      return () => {
        document.removeEventListener('closeTagForm', handleCloseTagForm);
      };
    }, []);

    // Function to add or update tag value
    const addTagValue = async () => {
      if (tagParams.currentTagKey.trim() !== '') {
        const newTagValue: TagValue = {
          description: tagParams.currentTagDescription,
          isActive: tagParams.currentTagIsActive,
        };

        const updatedValues = { ...tagParams.values };
        const trimmedKey = tagParams.currentTagKey.trim();

        // If editing an existing tag value
        if (editingTagKey) {
          // If the key changed, remove the old key first
          if (editingTagKey !== trimmedKey) {
            delete updatedValues[editingTagKey];
            // Check for duplicates with remaining keys
            if (checkForDuplicateTagValue(trimmedKey, updatedValues)) {
              setFormErrors((prev) => ({
                ...prev,
                duplicateTagValue: DUPLICATE_TAG_VALUE_ERROR,
              }));
              return;
            }
          }
          // Update with new/same key
          updatedValues[trimmedKey] = newTagValue;
        } else {
          // Adding new tag value - check for duplicates
          if (checkForDuplicateTagValue(trimmedKey, updatedValues)) {
            setFormErrors((prev) => ({
              ...prev,
              duplicateTagValue: DUPLICATE_TAG_VALUE_ERROR,
            }));
            return;
          }
          updatedValues[trimmedKey] = newTagValue;
        }

        // Only call API for existing tags, not new tags being created
        if (tag) {
          // Update existing tag - call API first, then update UI state only if successful
          const updateTagData: UpdateTagRequest = {
            businessUnitId: tagParams.businessUnitId,
            programId: tagParams.programId,
            tagId: tagParams.tagId,
            tagGroupName: tagParams.tagGroupName.trim(),
            description: tagParams.description,
            values: updatedValues,
            isActive: tagParams.isActive,
            isMandatoryForQuestions: tagParams.isMandatoryForQuestions,
            isMandatoryForAuditTemplates: tagParams.isMandatoryForAuditTemplates,
            isApplicableForAuditTemplate: tagParams.isApplicableForAuditTemplate,
            updatedBy: getUserId(),
            ...(tagParams.maxTagsPerTemplate !== null && {
              maxTagsPerTemplate: tagParams.maxTagsPerTemplate,
            }),
          };

          try {
            await updateTag(updateTagData).unwrap();
            // Only update UI state if API call was successful
            const updatedTagParams = {
              ...tagParams,
              values: updatedValues,
              currentTagKey: '',
              currentTagDescription: '',
              currentTagIsActive: true,
            };
            setTagParams(updatedTagParams);
            setEditingTagKey(null);
            setFormErrors((prev) => ({ ...prev, duplicateTagValue: null }));
          } catch (error) {
            // API call failed, don't update UI state - error will be shown via ApiStatusFlashBar
          }
        } else {
          // For new tags, update UI state immediately since validation happens on save
          const updatedTagParams = {
            ...tagParams,
            values: updatedValues,
            currentTagKey: '',
            currentTagDescription: '',
            currentTagIsActive: true,
          };
          setTagParams(updatedTagParams);
          setEditingTagKey(null);
          setFormErrors((prev) => ({ ...prev, duplicateTagValue: null }));
        }
      }
    };

    // Function to edit existing tag value
    const editTagValue = (key: string) => {
      const tagValue = tagParams.values[key];
      setTagParams({
        ...tagParams,
        currentTagKey: key,
        currentTagDescription: tagValue.description,
        currentTagIsActive: tagValue.isActive,
      });
      setEditingTagKey(key);
      // Clear any existing duplicate error when starting to edit
      setFormErrors((prev) => ({ ...prev, duplicateTagValue: null }));
    };

    // Function to cancel editing
    const cancelEdit = () => {
      setTagParams({
        ...tagParams,
        currentTagKey: '',
        currentTagDescription: '',
        currentTagIsActive: true,
      });
      setEditingTagKey(null);
      setFormErrors((prev) => ({ ...prev, duplicateTagValue: null }));
    };

    const removeTagValue = (key: string) => {
      const updatedValues = { ...tagParams.values };
      delete updatedValues[key];
      setTagParams({
        ...tagParams,
        values: updatedValues,
      });
    };

    // Reusable duplicate validation function
    const checkForDuplicateTagValue = (
      newKey: string,
      existingValues: ValuesMap,
      excludeKey?: string,
    ): boolean => {
      const normalizedNewKey = normalizeForComparison(newKey);
      if (!normalizedNewKey) return false;

      return Object.keys(existingValues).some(
        (key) => key !== excludeKey && normalizeForComparison(key) === normalizedNewKey,
      );
    };

    const handleTagValueInputChange = ({ detail }: { detail: { value: string } }) => {
      const newValue = detail.value;
      const oldNormalized = normalizeForComparison(tagParams.currentTagKey);
      const newNormalized = normalizeForComparison(newValue);

      setTagParams((prevState) => ({
        ...prevState,
        currentTagKey: newValue,
      }));

      // Real-time duplicate validation - only check if normalized value changed
      if (oldNormalized !== newNormalized) {
        const isDuplicate = checkForDuplicateTagValue(
          newValue,
          tagParams.values,
          editingTagKey || undefined,
        );
        setFormErrors((prev) => ({
          ...prev,
          duplicateTagValue: isDuplicate ? DUPLICATE_TAG_VALUE_ERROR : null,
        }));
      }
    };

    const renderActionButton = () => {
      const onCancelClick = () => {
        closeView();
        onViewDismiss();
      };

      const onSaveClick = () => {
        if (!isEditTag) return; // Prevent saving without permission
        if (validate()) {
          setIsSaving(true); // Set flag to indicate this is a save operation
          if (tag) {
            // Update existing tag
            const updateTagData: UpdateTagRequest = {
              businessUnitId: tagParams.businessUnitId,
              programId: tagParams.programId,
              tagId: tagParams.tagId,
              tagGroupName: tagParams.tagGroupName.trim(),
              description: tagParams.description,
              values: tagParams.values,
              isActive: tagParams.isActive,
              isMandatoryForQuestions: tagParams.isMandatoryForQuestions,
              isMandatoryForAuditTemplates: tagParams.isMandatoryForAuditTemplates,
              isApplicableForAuditTemplate: tagParams.isApplicableForAuditTemplate,
              updatedBy: getUserId(),
              ...(tagParams.maxTagsPerTemplate !== null && {
                maxTagsPerTemplate: tagParams.maxTagsPerTemplate,
              }),
            };
            updateTag(updateTagData);
          } else {
            // Create new tag
            const createTagData: CreateTagRequest = {
              businessUnitId: tagParams.businessUnitId,
              programId: tagParams.programId,
              tagGroupName: tagParams.tagGroupName.trim(),
              description: tagParams.description,
              values: tagParams.values,
              isActive: tagParams.isActive,
              isMandatoryForQuestions: tagParams.isMandatoryForQuestions,
              isMandatoryForAuditTemplates: tagParams.isMandatoryForAuditTemplates,
              isApplicableForAuditTemplate: tagParams.isApplicableForAuditTemplate,
              createdBy: getUserId(),
              ...(tagParams.maxTagsPerTemplate !== null && {
                maxTagsPerTemplate: tagParams.maxTagsPerTemplate,
              }),
            };
            createTag(createTagData);
          }
        }
      };

      return (
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            data-testid="cancel-button"
            formAction="none"
            variant="link"
            onClick={onCancelClick}
          >
            Cancel
          </Button>

          <Button
            data-testid="save-button"
            variant="primary"
            onClick={onSaveClick}
            loading={updateTagApiStatus.isLoading || createTagApiStatus.isLoading}
          >
            Save
          </Button>
        </SpaceBetween>
      );
    };

    const renderForm = () => {
      return (
        <form onSubmit={(e) => e.preventDefault()}>
          <Form actions={isEditTag() && renderActionButton()}>
            <SpaceBetween direction="vertical" size="l">
              <FormField
                data-testid="tagId-field"
                label={<RequiredFieldLabel label="Tag Group" />}
                errorText={
                  tag ? fieldValidationError.tagId : fieldValidationError.tagGroupName
                }
              >
                <Input
                  readOnly={!!tag || !isEditTag()} // Tag ID is read-only for everyone when editing
                  data-testid="tagId-input"
                  value={tagParams.tagGroupName}
                  onChange={({ detail }) =>
                    setTagParams((prevState) => ({
                      ...prevState,
                      tagGroupName: detail.value,
                    }))
                  }
                />
              </FormField>

              <FormField data-testid="businessUnitId-field" label="Business Unit">
                <Input
                  data-testid="businessUnit-input"
                  value={
                    businessUnitList.find(
                      (bu) => bu.businessUnitId === tagParams.businessUnitId,
                    )?.name || tagParams.businessUnitId
                  }
                  readOnly
                />
              </FormField>

              <FormField
                data-testid="description-field"
                label="Description"
                errorText={fieldValidationError.description}
              >
                <Textarea
                  data-testid="description-input"
                  readOnly={!isEditTag()}
                  value={tagParams.description}
                  onChange={({ detail }) =>
                    setTagParams((prevState) => ({
                      ...prevState,
                      description: detail.value,
                    }))
                  }
                />
              </FormField>

              <FormField
                data-testid="values-field"
                label={<RequiredFieldLabel label="Tag Values" />}
                description="Add tag values with their descriptions and status"
                errorText={formErrors.duplicateTagValue || fieldValidationError.values}
              >
                <SpaceBetween size="m">
                  <Input
                    placeholder="Tag Value"
                    value={tagParams.currentTagKey}
                    readOnly={!!editingTagKey || !isEditTag()}
                    onChange={handleTagValueInputChange}
                    onKeyDown={(e) => {
                      if (e.detail.key === 'Enter') {
                        e.preventDefault();
                        addTagValue();
                      }
                    }}
                  />
                  <Textarea
                    placeholder="Description for this tag value (optional)"
                    readOnly={!isEditTag()}
                    value={tagParams.currentTagDescription}
                    onChange={({ detail }) =>
                      setTagParams((prevState) => ({
                        ...prevState,
                        currentTagDescription: detail.value,
                      }))
                    }
                  />
                  <Checkbox
                    checked={tagParams.currentTagIsActive}
                    readOnly={!isEditTag()}
                    onChange={({ detail }) =>
                      setTagParams((prevState) => ({
                        ...prevState,
                        currentTagIsActive: detail.checked,
                      }))
                    }
                  >
                    Tag Value Active
                  </Checkbox>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      variant="primary"
                      onClick={addTagValue}
                      disabled={tagParams.currentTagKey.trim() === '' || !isEditTag()}
                    >
                      {editingTagKey ? 'Update Tag Value' : 'Add Tag Value'}
                    </Button>
                    {editingTagKey && (
                      <Button
                        variant="normal"
                        onClick={cancelEdit}
                        disabled={!isEditTag()}
                      >
                        Cancel
                      </Button>
                    )}
                  </SpaceBetween>
                </SpaceBetween>

                {Object.keys(tagParams.values).length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <SpaceBetween direction="horizontal" size="xs" alignItems="start">
                      {Object.entries(tagParams.values).map(([key, value]) => (
                        <div
                          key={`${key}-${value.isActive}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: value.isActive ? '#e8f5e8' : '#ffeaea',
                            border: `1px solid ${value.isActive ? '#4caf50' : '#f44336'}`,
                            borderRadius: '4px',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            color: value.isActive ? '#2e7d32' : '#c62828',
                          }}
                          onClick={() => {
                            if (isEditTag()) {
                              editTagValue(key);
                            }
                          }}
                        >
                          <span
                            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                          >
                            {key}{' '}
                            <span
                              style={{ color: value.isActive ? '#4caf50' : '#f44336' }}
                            >
                              <Icon
                                name={
                                  value.isActive ? 'status-positive' : 'status-negative'
                                }
                                size="inherit"
                              />
                            </span>
                          </span>
                        </div>
                      ))}
                    </SpaceBetween>
                  </div>
                )}
              </FormField>

              <FormField
                data-testid="maxTagsPerTemplate-field"
                label="Maximum Tags per Template"
                description="Enter the maximum number of tags allowed per template"
              >
                <Input
                  type="number"
                  readOnly={!isEditTag()}
                  data-testid="maxTagsPerTemplate-input"
                  value={tagParams.maxTagsPerTemplate?.toString() || ''}
                  onChange={({ detail }) => {
                    const value = detail.value === '' ? null : parseInt(detail.value, 10);
                    setTagParams((prevState) => ({
                      ...prevState,
                      maxTagsPerTemplate: value === null || isNaN(value) ? null : value,
                    }));
                  }}
                />
              </FormField>

              <FormField
                label="Tag Activation Status"
                constraintText="Enable or disable the tag using this switch."
              >
                <Toggle
                  data-testid="isActive-toggle"
                  readOnly={!isEditTag()}
                  checked={tagParams.isActive}
                  onChange={({ detail }) =>
                    setTagParams((prevState) => ({
                      ...prevState,
                      isActive: detail.checked,
                    }))
                  }
                >
                  {tagParams.isActive ? 'Active' : 'Inactive'}
                </Toggle>
              </FormField>

              <FormField label="Settings">
                <SpaceBetween size="xs">
                  <Checkbox
                    checked={tagParams.isMandatoryForQuestions}
                    readOnly={!isEditTag()}
                    onChange={({ detail }) =>
                      setTagParams((prevState) => ({
                        ...prevState,
                        isMandatoryForQuestions: detail.checked,
                      }))
                    }
                  >
                    Mandatory For Questions
                  </Checkbox>

                  <Checkbox
                    checked={tagParams.isMandatoryForAuditTemplates}
                    readOnly={!isEditTag()}
                    onChange={({ detail }) =>
                      setTagParams((prevState) => ({
                        ...prevState,
                        isMandatoryForAuditTemplates: detail.checked,
                      }))
                    }
                  >
                    Mandatory for Audit Templates
                  </Checkbox>
                </SpaceBetween>
              </FormField>
            </SpaceBetween>
          </Form>
        </form>
      );
    };

    return <>{renderForm()}</>;
  },
);

export default TagDetailsForm;
